<template>
  <div class="docs-page">
    <div class="mb-4">
      <h1 class="text-3xl font-bold mb-2">Documentation</h1>
      <p class="text-surface-600 dark:text-surface-400">
        Browse available documentation and guides
      </p>
    </div>

    <div class="grid grid-cols-12 gap-4">
      <!-- Sidebar with document list -->
      <div class="col-span-12 md:col-span-3">
        <Card>
          <template #title>
            <div class="text-lg">Available Docs</div>
          </template>
          <template #content>
            <div v-if="loadingList" class="flex justify-center py-4">
              <ProgressSpinner style="width: 30px; height: 30px" />
            </div>
            
            <div v-else-if="documents.length === 0" class="text-surface-500">
              No documents available
            </div>
            
            <div v-else class="flex flex-col gap-2">
              <Button
                v-for="doc in documents"
                :key="doc.filename"
                :label="doc.name"
                :severity="selectedDoc === doc.filename ? 'primary' : 'secondary'"
                :outlined="selectedDoc !== doc.filename"
                @click="selectDocument(doc.filename)"
                class="w-full justify-start"
              />
            </div>
          </template>
        </Card>
      </div>

      <!-- Main content area -->
      <div class="col-span-12 md:col-span-9">
        <MarkdownViewer v-if="selectedDoc" :filename="selectedDoc" />
        
        <Card v-else>
          <template #content>
            <div class="text-center py-8 text-surface-500">
              <i class="pi pi-book text-4xl mb-4"></i>
              <p class="text-lg">Select a document to view</p>
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {docsBackend, type DocumentInfo} from "@/backend/docs.backend.ts";
import MarkdownViewer from "@/components/MarkdownViewer.vue";
import Card from "primevue/card";
import Button from "primevue/button";
import ProgressSpinner from "primevue/progressspinner";

const route = useRoute();
const router = useRouter();

const loadingList = ref(false);
const documents = ref<DocumentInfo[]>([]);
const selectedDoc = ref<string | null>(null);

async function loadDocumentList() {
  loadingList.value = true;
  try {
    const result = await docsBackend.listDocuments();
    documents.value = result.documents;
    
    // Auto-select first document or one from route
    if (route.query.doc) {
      selectedDoc.value = route.query.doc as string;
    } else if (documents.value.length > 0) {
      selectedDoc.value = documents.value[0].filename;
    }
  } catch (err) {
    console.error("Error loading document list:", err);
  } finally {
    loadingList.value = false;
  }
}

function selectDocument(filename: string) {
  selectedDoc.value = filename;
  // Update URL query parameter
  router.push({query: {doc: filename}});
}

onMounted(() => {
  loadDocumentList();
});
</script>

<style scoped>
.docs-page {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}
</style>
